import { useState, useEffect } from 'react';
import { supabase, SUPABASE_URL, SUPABASE_KEY } from '../supabase';
import { C, fmt, getComprobanteUrl } from '../constants';

// TODO: mover CLABE_DEFAULT a tabla config en Supabase
const CLABE_DEFAULT = '728969000170148422';

// Modal definido fuera del componente para evitar re-mount
function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '28px 32px', width: 440, maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 18, fontWeight: 900, color: '#1E293B' }}>{title}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#94A3B8', padding: 4 }}>x</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── DASHBOARD ADMIN (SECRETO) ──────────────────────────────
function Dashboard() {
  const [autenticado, setAutenticado] = useState(false);
  const [usuario, setUsuario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seccion, setSeccion] = useState('tandas');
  const [tandas, setTandas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [solicitudes, setSolicitudes] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [toast, setToast] = useState('');
  const [pagoTandaExpandida, setPagoTandaExpandida] = useState(null);
  const [modalRechazoPago, setModalRechazoPago] = useState(null); // pago id
  const [motivoRechazoPago, setMotivoRechazoPago] = useState('');
  const [motivoRechazoPagoCustom, setMotivoRechazoPagoCustom] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [needsLogin, setNeedsLogin] = useState(false);

  // Tandas UI state
  const [modalCrear, setModalCrear] = useState(false);
  const [modalEditar, setModalEditar] = useState(null);
  const [formTanda, setFormTanda] = useState({ nombre: '', monto: '', semanas: '', clabe: CLABE_DEFAULT, nivel_minimo: 'Bronce', frecuencia: 'Semanal' });
  const [creandoTanda, setCreandoTanda] = useState(false);
  const [tandaExpandida, setTandaExpandida] = useState(null);
  const [modalAgregarUsuario, setModalAgregarUsuario] = useState(null); // tanda_id
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState('');
  const [modalAvanzar, setModalAvanzar] = useState(null); // tanda obj
  const [pagosSemana, setPagosSemana] = useState([]); // pagos de la semana actual para modal
  const [alertasTandas, setAlertasTandas] = useState({}); // { tanda_id: { tipo, msg } }

  // Usuarios UI state
  const [modalNivel, setModalNivel] = useState(null); // usuario obj
  const [modalAmonestar, setModalAmonestar] = useState(null); // usuario obj
  const [motivoAmonestacion, setMotivoAmonestacion] = useState('');
  const [modalRechazoDoc, setModalRechazoDoc] = useState(null); // { usuario, tipo }
  const [motivoRechazoDoc, setMotivoRechazoDoc] = useState('');
  const [motivoRechazoCustom, setMotivoRechazoCustom] = useState('');
  const [usuarioExpandido, setUsuarioExpandido] = useState(null); // usuario id
  const [historialUsuario, setHistorialUsuario] = useState([]); // participaciones del usuario expandido
  const [amonestacionesUsuario, setAmonestacionesUsuario] = useState([]); // amonestaciones

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const verificarAdmin = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { setNeedsLogin(true); setLoading(false); return; }
      const { data } = await supabase.from('usuarios').select('*').eq('id', session.user.id).single();
      if (data?.rol === 'admin') {
        setUsuario(data);
        setAutenticado(true);
        setNeedsLogin(false);
        await cargarTodo();
      }
    } catch (err) {
      console.error('Error verificarAdmin:', err);
    }
    setLoading(false);
  };

  const handleLogin = async () => {
    setLoginLoading(true); setLoginError('');
    const { error } = await supabase.auth.signInWithPassword({ email: loginEmail, password: loginPass });
    if (error) { setLoginError('Email o contraseña incorrectos'); setLoginLoading(false); return; }
    setLoginLoading(false);
    setLoading(true);
    await verificarAdmin();
  };

  useEffect(() => {
    verificarAdmin();
  }, []); // eslint-disable-line

  const cargarTodo = async () => {
    // Cargar tandas y participantes por separado para evitar problemas con nested joins
    const [t, u, s, p, parts] = await Promise.all([
      supabase.from('tandas').select('*').order('created_at', { ascending: false }),
      supabase.from('usuarios').select('*').order('created_at', { ascending: false }),
      supabase.from('auditoria').select('*').eq('accion', 'solicitud_unirse').order('created_at', { ascending: false }),
      supabase.from('pagos').select('*').not('comprobante_url', 'is', null).order('created_at', { ascending: false }),
      supabase.from('participantes').select('id, turno, usuario_id, tanda_id, usuarios(nombre, telefono, metodo_cobro, clabe, banco, nombre_titular)'),
    ]);
    // Unir participantes a sus tandas manualmente
    const tandasConParts = (t.data || []).map(tanda => ({
      ...tanda,
      participantes: (parts.data || []).filter(p => p.tanda_id === tanda.id),
    }));
    setTandas(tandasConParts);
    setUsuarios(u.data || []);
    setSolicitudes(s.data || []);
    // Unir pagos con participantes manualmente
    const pagosConParts = (p.data || []).map(pago => {
      const part = (parts.data || []).find(pt => pt.id === pago.participante_id);
      const tanda = (t.data || []).find(td => td.id === part?.tanda_id);
      return { ...pago, participantes: part ? { ...part, tandas: tanda ? { nombre: tanda.nombre, monto: tanda.monto } : null } : null };
    });
    setPagos(pagosConParts);

    // Auto-check: verificar si es lunes y si se pueden avanzar semanas
    const hoy = new Date();
    const esLunes = hoy.getDay() === 1;
    const tandasActivas = (t.data || []).filter(td => td.estado === 'activa' && td.semana_actual < td.semanas);
    const alertas = {};

    // Obtener todos los pagos de tandas activas en UNA sola query (evita N+1)
    const allPartIds = tandasActivas.flatMap(td => (td.participantes || []).map(p => p.id));
    let todosPagos = [];
    if (allPartIds.length > 0) {
      const { data: pagosData } = await supabase.from('pagos')
        .select('participante_id, pagado, semana')
        .in('participante_id', allPartIds);
      todosPagos = pagosData || [];
    }

    for (const tanda of tandasActivas) {
      const participantes = tanda.participantes || [];
      if (participantes.length === 0) continue;
      const partIds = new Set(participantes.map(p => p.id));
      const pagosTanda = todosPagos.filter(p => partIds.has(p.participante_id) && p.semana === tanda.semana_actual);
      const pagosConfirmados = pagosTanda.filter(p => p.pagado).length;
      const totalParticipantes = participantes.length;

      if (pagosConfirmados === totalParticipantes && totalParticipantes > 0) {
        alertas[tanda.id] = { tipo: 'listo', msg: `Todos los pagos confirmados (${pagosConfirmados}/${totalParticipantes}). Confirma el pago al ganador para avanzar de semana.` };
      } else if (esLunes && pagosConfirmados < totalParticipantes) {
        alertas[tanda.id] = { tipo: 'retraso', msg: `Retraso: solo ${pagosConfirmados}/${totalParticipantes} pagos confirmados esta semana` };
      } else {
        const pendientes = totalParticipantes - pagosConfirmados;
        if (pendientes > 0) {
          alertas[tanda.id] = { tipo: 'pendiente', msg: `${pagosConfirmados}/${totalParticipantes} pagos confirmados · ${pendientes} pendientes` };
        }
      }
    }
    setAlertasTandas(alertas);
  };

  const crearTanda = async () => {
    const { nombre, monto, semanas, clabe } = formTanda;
    if (!nombre || !monto || !semanas) { showToast('Completa nombre, monto y semanas'); return; }
    if (clabe && !/^\d{18}$/.test(clabe)) { showToast('CLABE debe tener 18 dígitos'); return; }
    setCreandoTanda(true);
    const { error } = await supabase.from('tandas').insert({
      nombre: nombre.trim(), monto: Number(monto), semanas: Number(semanas),
      clabe: clabe || CLABE_DEFAULT, semana_actual: 1, estado: 'pendiente',
      nivel_minimo: formTanda.nivel_minimo || 'Bronce',
      frecuencia: formTanda.frecuencia || 'Semanal',
    });
    setCreandoTanda(false);
    if (error) { showToast('Error: ' + error.message); return; }
    setFormTanda({ nombre: '', monto: '', semanas: '', clabe: CLABE_DEFAULT, nivel_minimo: 'Bronce', frecuencia: 'Semanal' });
    setModalCrear(false);
    showToast('Tanda creada');
    cargarTodo();
  };

  const editarTanda = async () => {
    const { nombre, monto, semanas, clabe } = formTanda;
    if (!nombre || !monto || !semanas) { showToast('Completa nombre, monto y semanas'); return; }
    if (clabe && !/^\d{18}$/.test(clabe)) { showToast('CLABE debe tener 18 dígitos'); return; }
    const { error } = await supabase.from('tandas').update({
      nombre: nombre.trim(), monto: Number(monto), semanas: Number(semanas),
      nivel_minimo: formTanda.nivel_minimo || 'Bronce',
      frecuencia: formTanda.frecuencia || 'Semanal',
      clabe: clabe || null,
    }).eq('id', modalEditar);
    if (error) { showToast('Error: ' + error.message); return; }
    setModalEditar(null);
    showToast('Tanda actualizada');
    cargarTodo();
  };

  const eliminarTanda = async (tanda) => {
    if (tanda.estado === 'activa') {
      showToast('No se puede eliminar una tanda activa. Termínala primero.');
      return;
    }
    if (!window.confirm(`¿Eliminar "${tanda.nombre}"? Esta acción no se puede deshacer.`)) return;
    await supabase.from('participantes').delete().eq('tanda_id', tanda.id);
    const { error } = await supabase.from('tandas').delete().eq('id', tanda.id);
    if (error) { showToast('Error: ' + error.message); return; }
    showToast('Tanda eliminada');
    cargarTodo();
  };

  const abrirModalAvanzar = async (tanda) => {
    // Cargar pagos de la semana actual para mostrar en el modal
    const participantes = tanda.participantes || [];
    if (participantes.length === 0) { setPagosSemana([]); setModalAvanzar(tanda); return; }
    const { data } = await supabase.from('pagos')
      .select('participante_id, pagado, comprobante_url, estado_comprobante')
      .in('participante_id', participantes.map(p => p.id))
      .eq('semana', tanda.semana_actual);
    // Mapear con nombres
    const pagosMap = {};
    (data || []).forEach(p => { pagosMap[p.participante_id] = p; });
    const resumen = participantes.map(p => ({
      nombre: p.usuarios?.nombre || '—',
      turno: p.turno,
      pagado: pagosMap[p.id]?.pagado || false,
      comprobante: !!pagosMap[p.id]?.comprobante_url,
      estado: pagosMap[p.id]?.estado_comprobante || null,
    }));
    setPagosSemana(resumen);
    setModalAvanzar(tanda);
  };

  const confirmarAvanzar = async () => {
    const t = modalAvanzar;
    if (t.semana_actual >= t.semanas) { showToast('Ya terminó'); return; }
    // Verificar que todos pagaron antes de avanzar
    const participantes = t.participantes || [];
    if (participantes.length > 0) {
      const { data: pagosSemana } = await supabase.from('pagos')
        .select('participante_id, pagado')
        .in('participante_id', participantes.map(p => p.id))
        .eq('semana', t.semana_actual);
      const pagados = (pagosSemana || []).filter(p => p.pagado).length;
      if (pagados < participantes.length) {
        if (!window.confirm(`Solo ${pagados}/${participantes.length} pagos confirmados esta semana. ¿Avanzar de todas formas?`)) return;
      }
    }
    await supabase.from('tandas').update({ semana_actual: t.semana_actual + 1 }).eq('id', t.id);
    setModalAvanzar(null);
    showToast(`Semana avanzada a ${t.semana_actual + 1}`);
    cargarTodo();
  };

  // Auto-sorteo: ejecutado en Edge Function del backend (criptográfico + atómico)
  const autoSorteo = async (tandaId) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { showToast('Sesión expirada'); return; }
      const resp = await fetch(`${SUPABASE_URL}/functions/v1/sorteo-tanda`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': SUPABASE_KEY,
        },
        body: JSON.stringify({ tanda_id: tandaId }),
      });
      const data = await resp.json();
      if (!resp.ok) { showToast(data.error || 'Error en sorteo'); return; }
      showToast(data.message || 'Tanda llena — turnos asignados automáticamente');
    } catch (err) {
      showToast('Error: ' + err.message);
    }
  };

  const [agregando, setAgregando] = useState(false);
  const agregarUsuarioATanda = async (tandaId) => {
    if (!usuarioSeleccionado) { showToast('Selecciona un usuario'); return; }
    if (agregando) return;
    setAgregando(true);
    // Verificar si ya está en la tanda
    const { data: existe } = await supabase.from('participantes').select('id').eq('usuario_id', usuarioSeleccionado).eq('tanda_id', tandaId).maybeSingle();
    if (existe) { showToast('Este usuario ya está en la tanda'); setAgregando(false); return; }
    const { error } = await supabase.from('participantes').insert({ usuario_id: usuarioSeleccionado, tanda_id: tandaId });
    setAgregando(false);
    if (error) { showToast('Error: ' + error.message); return; }
    setModalAgregarUsuario(null);
    setUsuarioSeleccionado('');
    // Verificar si la tanda se llenó (consultar DB directo)
    const { data: tandaDB } = await supabase.from('tandas').select('semanas').eq('id', tandaId).single();
    const { data: allParts } = await supabase.from('participantes').select('id').eq('tanda_id', tandaId);
    if (tandaDB && allParts && allParts.length >= tandaDB.semanas) {
      await autoSorteo(tandaId);
    } else {
      showToast('Usuario agregado a la tanda');
    }
    cargarTodo();
  };

  const quitarParticipante = async (participanteId, nombre, usuarioId, tandaId) => {
    if (!window.confirm(`¿Quitar a ${nombre || 'este participante'} de la tanda?`)) return;
    // Intentar por id primero, luego por usuario_id + tanda_id
    let { error } = await supabase.from('participantes').delete().eq('id', participanteId);
    if (error) {
      // Fallback: intentar por usuario_id y tanda_id
      const res = await supabase.from('participantes').delete().eq('usuario_id', usuarioId).eq('tanda_id', tandaId);
      error = res.error;
    }
    if (error) { showToast('Error: no tienes permiso para quitar. Agrega política DELETE en participantes para admin en Supabase.'); return; }
    showToast('Participante removido');
    cargarTodo();
  };

  const cambiarNivel = async (userId, nuevoNivel) => {
    await supabase.from('usuarios').update({ nivel: nuevoNivel }).eq('id', userId);
    setModalNivel(null);
    showToast('Nivel actualizado a ' + nuevoNivel);
    cargarTodo();
  };

  const amonestarUsuario = async (usr) => {
    if (!motivoAmonestacion.trim()) { showToast('Escribe un motivo'); return; }
    await supabase.from('auditoria').insert({
      usuario_id: usr.id,
      accion: 'amonestacion',
      detalle: JSON.stringify({ motivo: motivoAmonestacion, admin: usuario.nombre, fecha: new Date().toISOString() }),
    });
    setModalAmonestar(null);
    setMotivoAmonestacion('');
    showToast('Amonestación registrada');
  };

  const inhabilitarUsuario = async (usr) => {
    const accion = usr.rol === 'inhabilitado' ? 'rehabilitar' : 'inhabilitar';
    if (!window.confirm(`¿${accion === 'inhabilitar' ? 'Inhabilitar' : 'Rehabilitar'} a ${usr.nombre}?`)) return;
    const nuevoRol = accion === 'inhabilitar' ? 'inhabilitado' : 'user';
    await supabase.from('usuarios').update({ rol: nuevoRol }).eq('id', usr.id);
    await supabase.from('auditoria').insert({
      usuario_id: usr.id,
      accion: accion === 'inhabilitar' ? 'usuario_inhabilitado' : 'usuario_rehabilitado',
      detalle: JSON.stringify({ admin: usuario.nombre, fecha: new Date().toISOString() }),
    });
    showToast(`Usuario ${accion === 'inhabilitar' ? 'inhabilitado' : 'rehabilitado'}`);
    cargarTodo();
  };

  const darDeBajaUsuario = async (usr) => {
    if (!window.confirm(`¿Dar de BAJA DEFINITIVA a ${usr.nombre}? Esta acción no se puede deshacer.`)) return;
    // Eliminar participaciones
    await supabase.from('participantes').delete().eq('usuario_id', usr.id);
    await supabase.from('usuarios').delete().eq('id', usr.id);
    await supabase.from('auditoria').insert({
      usuario_id: usr.id,
      accion: 'usuario_baja_definitiva',
      detalle: JSON.stringify({ nombre: usr.nombre, admin: usuario.nombre, fecha: new Date().toISOString() }),
    });
    showToast('Usuario dado de baja');
    cargarTodo();
  };

  const expandirUsuario = async (usr) => {
    if (usuarioExpandido === usr.id) { setUsuarioExpandido(null); return; }
    setUsuarioExpandido(usr.id);
    // Cargar participaciones con info de tanda y pagos
    const { data: parts } = await supabase.from('participantes')
      .select('*, tandas(nombre, monto, semanas, semana_actual, estado), pagos(semana, pagado, fecha_pago)')
      .eq('usuario_id', usr.id);
    setHistorialUsuario(parts || []);
    // Cargar amonestaciones
    const { data: amon } = await supabase.from('auditoria')
      .select('*')
      .eq('usuario_id', usr.id)
      .in('accion', ['amonestacion', 'usuario_inhabilitado', 'usuario_rehabilitado'])
      .order('created_at', { ascending: false });
    setAmonestacionesUsuario(amon || []);
  };

  const asignarNumCliente = async (usr) => {
    // Generar número de 5 dígitos único
    const existentes = usuarios.map(u => u.codigo_ref).filter(Boolean);
    let num;
    do { num = String(Math.floor(100000 + Math.random() * 900000)); } while (existentes.includes(num));
    await supabase.from('usuarios').update({ codigo_ref: num }).eq('id', usr.id);
    showToast('No. Cliente asignado: ' + num);
    cargarTodo();
  };

  const aprobarDocumentos = async (usr) => {
    await supabase.from('usuarios').update({ verificacion_identidad: 'aprobada' }).eq('id', usr.id);
    await supabase.from('auditoria').insert({
      usuario_id: usr.id, accion: 'verificacion_aprobada',
      detalle: JSON.stringify({ admin: usuario.nombre, fecha: new Date().toISOString() }),
    });
    showToast('Documentos aprobados');
    cargarTodo();
  };

  const MOTIVOS_RECHAZO_PAGO = [
    'Imagen no legible o borrosa',
    'Monto incorrecto',
    'Fecha incorrecta — comprobante no es de este periodo',
    'Cuenta de destino incorrecta',
    'Comprobante duplicado — ya fue registrado antes',
    'No corresponde a una transferencia bancaria',
    'Nombre del titular no coincide',
    'Comprobante inválido o alterado',
  ];

  const confirmarRechazoPago = async () => {
    const motivo = motivoRechazoPago === 'otro' ? motivoRechazoPagoCustom : motivoRechazoPago;
    if (!motivo) { showToast('Selecciona o escribe un motivo'); return; }
    await supabase.from('pagos').update({ comprobante_url: null, estado_comprobante: 'rechazado', motivo_rechazo: motivo }).eq('id', modalRechazoPago);
    setModalRechazoPago(null);
    setMotivoRechazoPago('');
    setMotivoRechazoPagoCustom('');
    showToast('Pago rechazado');
    cargarTodo();
  };

  const MOTIVOS_RECHAZO_DOC = [
    'Imagen no legible o borrosa',
    'INE vencida o no válida',
    'La selfie no coincide con la INE',
    'Documento incompleto — falta frente o reverso',
    'Nombre en INE no coincide con el registro',
    'Foto recortada o no se ven todos los datos',
  ];

  const confirmarRechazoDoc = async () => {
    const motivo = motivoRechazoDoc === 'otro' ? motivoRechazoCustom : motivoRechazoDoc;
    if (!motivo) { showToast('Selecciona o escribe un motivo'); return; }
    const { usuario: usr, campo, tipo } = modalRechazoDoc;
    const updateData = { [campo]: 'rechazado' };
    if (campo) updateData[campo + '_motivo'] = motivo;
    await supabase.from('usuarios').update(updateData).eq('id', usr.id);
    await supabase.from('auditoria').insert({
      usuario_id: usr.id, accion: 'verificacion_rechazada',
      detalle: JSON.stringify({ documento: tipo, motivo, admin: usuario.nombre, fecha: new Date().toISOString() }),
    });
    setModalRechazoDoc(null);
    setMotivoRechazoDoc('');
    setMotivoRechazoCustom('');
    showToast((tipo || 'Documento') + ' rechazado');
    cargarTodo();
  };

  const NIVELES = ['Bronce', 'Plata', 'Oro', 'Platino'];

  const abrirEditar = (t) => {
    setFormTanda({ nombre: t.nombre, monto: String(t.monto), semanas: String(t.semanas), clabe: t.clabe || CLABE_DEFAULT, nivel_minimo: t.nivel_minimo || 'Bronce', frecuencia: t.frecuencia || 'Semanal' });
    setModalEditar(t.id);
  };

  if (loading) return <div style={S.center}>Cargando...</div>;
  if (needsLogin || !autenticado) return (
    <div style={S.center}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '40px 32px', boxShadow: '0 4px 24px rgba(0,0,0,0.1)', width: 360, textAlign: 'center' }}>
        <div style={{ fontSize: 24, fontWeight: 900, color: '#1E293B', marginBottom: 4 }}>TandaClick</div>
        <div style={{ fontSize: 13, color: '#94A3B8', marginBottom: 24 }}>Panel de administración</div>
        <input placeholder="Email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)}
          style={{ ...S.input, width: '100%', padding: '12px 14px', marginBottom: 12, fontSize: 14 }} />
        <input placeholder="Contraseña" type="password" value={loginPass} onChange={e => setLoginPass(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleLogin()}
          style={{ ...S.input, width: '100%', padding: '12px 14px', marginBottom: 16, fontSize: 14 }} />
        {loginError && <div style={{ color: '#DC2626', fontSize: 13, marginBottom: 12 }}>{loginError}</div>}
        <button onClick={handleLogin} disabled={loginLoading} style={{ ...S.btnPrimary, width: '100%', padding: '12px 0', fontSize: 14 }}>
          {loginLoading ? 'Entrando...' : 'Entrar'}
        </button>
      </div>
    </div>
  );

  const secciones = [
    { id: 'tandas', label: 'Tandas', icon: '🤝', count: null },
    { id: 'usuarios', label: 'Usuarios', icon: '👥', count: null },
    { id: 'solicitudes', label: 'Solicitudes', icon: '📩', count: solicitudes.length || null },
    { id: 'pagos', label: 'Pagos', icon: '💸', count: pagos.filter(p => !p.pagado && p.estado_comprobante !== 'rechazado').length || null },
  ];

  const FRECUENCIAS_OPT = ['Semanal', 'Quincenal', 'Mensual'];
  const frecLabels = { 'Semanal': { monto: 'Monto semanal ($)', periodos: 'Semanas' }, 'Quincenal': { monto: 'Monto quincenal ($)', periodos: 'Quincenas' }, 'Mensual': { monto: 'Monto mensual ($)', periodos: 'Meses' } };
  const frecActual = frecLabels[formTanda.frecuencia] || frecLabels['Semanal'];

  const renderTandaFormBody = (onSubmit, submitLabel) => (
    <>
      {/* Nombre */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#64748B', marginBottom: 4 }}>Nombre de la tanda</div>
        <input type="text" placeholder="Ej: Tanda Familiar" value={formTanda.nombre}
          onChange={e => setFormTanda(p => ({ ...p, nombre: e.target.value }))}
          style={{ ...S.input, width: '100%', padding: '10px 14px', fontSize: 14 }} />
      </div>
      {/* Frecuencia */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#64748B', marginBottom: 4 }}>Frecuencia</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {FRECUENCIAS_OPT.map(f => (
            <button key={f} onClick={() => setFormTanda(p => ({ ...p, frecuencia: f }))} style={{
              flex: 1, padding: '10px 0', borderRadius: 8, border: formTanda.frecuencia === f ? 'none' : '1.5px solid #E2E8F0',
              background: formTanda.frecuencia === f ? '#1A56DB' : '#fff',
              color: formTanda.frecuencia === f ? '#fff' : '#475569',
              fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}>{f}</button>
          ))}
        </div>
      </div>
      {/* Monto */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#64748B', marginBottom: 4 }}>{frecActual.monto}</div>
        <input type="number" placeholder="200" value={formTanda.monto}
          onChange={e => setFormTanda(p => ({ ...p, monto: e.target.value }))}
          style={{ ...S.input, width: '100%', padding: '10px 14px', fontSize: 14 }} />
      </div>
      {/* Periodos */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#64748B', marginBottom: 4 }}>{frecActual.periodos}</div>
        <input type="number" placeholder="10" value={formTanda.semanas}
          onChange={e => setFormTanda(p => ({ ...p, semanas: e.target.value }))}
          style={{ ...S.input, width: '100%', padding: '10px 14px', fontSize: 14 }} />
      </div>
      {/* CLABE */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#64748B', marginBottom: 4 }}>CLABE (18 dígitos)</div>
        <input type="tel" placeholder={CLABE_DEFAULT} value={formTanda.clabe}
          onChange={e => setFormTanda(p => ({ ...p, clabe: e.target.value.replace(/\D/g, '').slice(0, 18) }))}
          style={{ ...S.input, width: '100%', padding: '10px 14px', fontSize: 14 }} />
      </div>
      {/* Nivel mínimo */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#64748B', marginBottom: 4 }}>Nivel mínimo requerido</div>
        <select value={formTanda.nivel_minimo || 'Bronce'} onChange={e => setFormTanda(p => ({ ...p, nivel_minimo: e.target.value }))}
          style={{ ...S.input, width: '100%', padding: '10px 14px', fontSize: 14 }}>
          {NIVELES.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
      {formTanda.monto && formTanda.semanas && (
        <div style={{ background: '#EBF0FF', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, fontWeight: 700, color: '#1A56DB' }}>
          Tanda completa: {fmt(Number(formTanda.monto) * Number(formTanda.semanas))} · {formTanda.frecuencia || 'Semanal'} · {formTanda.semanas} {frecActual.periodos.toLowerCase()}
        </div>
      )}
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={() => { setModalCrear(false); setModalEditar(null); }} style={{ ...S.refreshBtn, flex: 1, padding: '10px 0' }}>Cancelar</button>
        <button onClick={onSubmit} disabled={creandoTanda} style={{ ...S.btnPrimary, flex: 1, padding: '10px 0', fontSize: 14 }}>
          {creandoTanda ? 'Guardando...' : submitLabel}
        </button>
      </div>
    </>
  );

  return (
    <div style={S.layout}>
      {toast && <div style={S.toast}>{toast}</div>}

      {/* Modal crear tanda */}
      {modalCrear && (
        <Modal title="Crear nueva tanda" onClose={() => setModalCrear(false)}>
          {renderTandaFormBody(crearTanda, 'Crear tanda')}
        </Modal>
      )}

      {/* Modal editar tanda */}
      {modalEditar && (
        <Modal title="Editar tanda" onClose={() => setModalEditar(null)}>
          {renderTandaFormBody(editarTanda, 'Guardar cambios')}
        </Modal>
      )}

      {/* Modal agregar usuario a tanda */}
      {modalAgregarUsuario && (
        <Modal title="Agregar usuario a tanda" onClose={() => setModalAgregarUsuario(null)}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748B', marginBottom: 4 }}>Selecciona un usuario</div>
            <select value={usuarioSeleccionado} onChange={e => setUsuarioSeleccionado(e.target.value)}
              style={{ ...S.input, width: '100%', padding: '10px 14px', fontSize: 14 }}>
              <option value="">— Seleccionar —</option>
              {usuarios.filter(u => u.rol !== 'inhabilitado').map(u => (
                <option key={u.id} value={u.id}>{u.nombre} ({u.telefono})</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setModalAgregarUsuario(null)} style={{ ...S.refreshBtn, flex: 1, padding: '10px 0' }}>Cancelar</button>
            <button onClick={() => agregarUsuarioATanda(modalAgregarUsuario)} style={{ ...S.btnPrimary, flex: 1, padding: '10px 0', fontSize: 14 }}>Agregar</button>
          </div>
        </Modal>
      )}

      {/* Modal cambiar nivel */}
      {modalNivel && (
        <Modal title={`Cambiar nivel de ${modalNivel.nombre}`} onClose={() => setModalNivel(null)}>
          <div style={{ fontSize: 13, color: '#64748B', marginBottom: 16 }}>Nivel actual: <strong>{modalNivel.nivel || 'Bronce'}</strong></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {NIVELES.map(n => (
              <button key={n} onClick={() => cambiarNivel(modalNivel.id, n)} style={{
                padding: '12px 16px', borderRadius: 10, border: (modalNivel.nivel || 'Bronce') === n ? '2px solid #1A56DB' : '1.5px solid #E2E8F0',
                background: (modalNivel.nivel || 'Bronce') === n ? '#EBF0FF' : '#fff',
                color: '#1E293B', fontSize: 14, fontWeight: 700, cursor: 'pointer', textAlign: 'left',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}>
                {n.includes('Bronce') ? '🥉' : n.includes('Plata') ? '🥈' : n.includes('Oro') ? '🥇' : '💎'} {n}
                {(modalNivel.nivel || 'Bronce') === n && ' (actual)'}
              </button>
            ))}
          </div>
        </Modal>
      )}

      {/* Modal confirmar avance de semana */}
      {modalAvanzar && (
        <Modal title={`Avanzar semana — ${modalAvanzar.nombre}`} onClose={() => setModalAvanzar(null)}>
          <div style={{ fontSize: 14, color: '#475569', marginBottom: 16 }}>
            Vas a avanzar de <strong>semana {modalAvanzar.semana_actual}</strong> a <strong>semana {modalAvanzar.semana_actual + 1}</strong>.
          </div>

          {/* Resumen de pagos */}
          <div style={{ fontSize: 12, fontWeight: 700, color: '#64748B', marginBottom: 8, textTransform: 'uppercase' }}>Estado de pagos — Semana {modalAvanzar.semana_actual}</div>
          <div style={{ maxHeight: 250, overflowY: 'auto', marginBottom: 16 }}>
            {pagosSemana.length === 0 ? (
              <div style={{ fontSize: 13, color: '#94A3B8', padding: 8 }}>Sin participantes</div>
            ) : pagosSemana.map((p, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: i % 2 === 0 ? '#F8FAFC' : '#fff', borderRadius: 6 }}>
                <span style={{ fontSize: 16 }}>{p.pagado ? '✅' : p.comprobante ? '⏳' : '❌'}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1E293B' }}>{p.nombre}</div>
                  <div style={{ fontSize: 11, color: '#94A3B8' }}>Turno #{p.turno || '—'}</div>
                </div>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                  background: p.pagado ? '#D1FAE5' : p.comprobante ? '#FEF3C7' : '#FEE2E2',
                  color: p.pagado ? '#059669' : p.comprobante ? '#D97706' : '#DC2626',
                }}>{p.pagado ? 'Confirmado' : p.comprobante ? 'En revisión' : 'Sin pago'}</span>
              </div>
            ))}
          </div>

          {/* Warning si hay pagos pendientes */}
          {pagosSemana.some(p => !p.pagado) && (
            <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#92400E' }}>
              ⚠️ Hay participantes sin pago confirmado. ¿Seguro que quieres avanzar de semana?
            </div>
          )}

          {pagosSemana.every(p => p.pagado) && pagosSemana.length > 0 && (
            <div style={{ background: '#D1FAE5', border: '1px solid #BBF7D0', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#065F46' }}>
              ✅ Todos los pagos están confirmados. Puedes avanzar sin problema.
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setModalAvanzar(null)} style={{ ...S.refreshBtn, flex: 1, padding: '10px 0' }}>Cancelar</button>
            <button onClick={confirmarAvanzar} style={{ ...S.btnPrimary, flex: 1, padding: '10px 0', fontSize: 14 }}>
              Confirmar avance
            </button>
          </div>
        </Modal>
      )}

      {/* Modal amonestar */}
      {modalAmonestar && (
        <Modal title={`Amonestar a ${modalAmonestar.nombre}`} onClose={() => setModalAmonestar(null)}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748B', marginBottom: 4 }}>Motivo de la amonestación</div>
            <textarea value={motivoAmonestacion} onChange={e => setMotivoAmonestacion(e.target.value)}
              placeholder="Describe el motivo de la amonestación..."
              rows={4}
              style={{ ...S.input, width: '100%', padding: '10px 14px', fontSize: 14, resize: 'vertical', fontFamily: "'Plus Jakarta Sans', sans-serif" }} />
          </div>
          <div style={{ background: '#FEF3C7', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#92400E' }}>
            Se registrará con fecha y hora: <strong>{new Date().toLocaleString('es-MX')}</strong>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setModalAmonestar(null)} style={{ ...S.refreshBtn, flex: 1, padding: '10px 0' }}>Cancelar</button>
            <button onClick={() => amonestarUsuario(modalAmonestar)} style={{ ...S.btnPrimary, flex: 1, padding: '10px 0', fontSize: 14, background: '#D97706' }}>Registrar amonestación</button>
          </div>
        </Modal>
      )}

      {/* Modal rechazo documentos */}
      {modalRechazoDoc && (
        <Modal title={`Rechazar documentos de ${modalRechazoDoc.usuario.nombre}`} onClose={() => setModalRechazoDoc(null)}>
          <div style={{ fontSize: 13, color: '#64748B', marginBottom: 14 }}>Selecciona el motivo del rechazo:</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            {[...MOTIVOS_RECHAZO_DOC, 'otro'].map(m => (
              <button key={m} onClick={() => { setMotivoRechazoDoc(m); if (m !== 'otro') setMotivoRechazoCustom(''); }} style={{
                padding: '10px 14px', borderRadius: 10, textAlign: 'left', cursor: 'pointer',
                border: motivoRechazoDoc === m ? '2px solid #DC2626' : '1.5px solid #E2E8F0',
                background: motivoRechazoDoc === m ? '#FEF2F2' : '#fff',
                color: '#1E293B', fontSize: 13, fontWeight: motivoRechazoDoc === m ? 700 : 500,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}>
                {m === 'otro' ? 'Otro motivo...' : m}
              </button>
            ))}
          </div>
          {motivoRechazoDoc === 'otro' && (
            <textarea value={motivoRechazoCustom} onChange={e => setMotivoRechazoCustom(e.target.value)}
              placeholder="Escribe el motivo del rechazo..."
              rows={3}
              style={{ ...S.input, width: '100%', padding: '10px 14px', fontSize: 14, resize: 'vertical', fontFamily: "'Plus Jakarta Sans', sans-serif", marginBottom: 14 }} />
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setModalRechazoDoc(null)} style={{ ...S.refreshBtn, flex: 1, padding: '10px 0' }}>Cancelar</button>
            <button onClick={confirmarRechazoDoc} style={{ ...S.btnPrimary, flex: 1, padding: '10px 0', fontSize: 14, background: '#DC2626' }}>Confirmar rechazo</button>
          </div>
        </Modal>
      )}

      {/* Modal rechazo pago */}
      {modalRechazoPago && (
        <Modal title="Rechazar comprobante" onClose={() => setModalRechazoPago(null)}>
          <div style={{ fontSize: 13, color: '#64748B', marginBottom: 14 }}>Selecciona el motivo del rechazo:</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            {[...MOTIVOS_RECHAZO_PAGO, 'otro'].map(m => (
              <button key={m} onClick={() => { setMotivoRechazoPago(m); if (m !== 'otro') setMotivoRechazoPagoCustom(''); }} style={{
                padding: '10px 14px', borderRadius: 10, textAlign: 'left', cursor: 'pointer',
                border: motivoRechazoPago === m ? '2px solid #DC2626' : '1.5px solid #E2E8F0',
                background: motivoRechazoPago === m ? '#FEF2F2' : '#fff',
                color: '#1E293B', fontSize: 13, fontWeight: motivoRechazoPago === m ? 700 : 500,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}>
                {m === 'otro' ? 'Otro motivo...' : m}
              </button>
            ))}
          </div>
          {motivoRechazoPago === 'otro' && (
            <textarea value={motivoRechazoPagoCustom} onChange={e => setMotivoRechazoPagoCustom(e.target.value)}
              placeholder="Escribe el motivo del rechazo..."
              rows={3}
              style={{ ...S.input, width: '100%', padding: '10px 14px', fontSize: 14, resize: 'vertical', fontFamily: "'Plus Jakarta Sans', sans-serif", marginBottom: 14 }} />
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setModalRechazoPago(null)} style={{ ...S.refreshBtn, flex: 1, padding: '10px 0' }}>Cancelar</button>
            <button onClick={confirmarRechazoPago} style={{ ...S.btnPrimary, flex: 1, padding: '10px 0', fontSize: 14, background: '#DC2626' }}>Confirmar rechazo</button>
          </div>
        </Modal>
      )}

      {/* Sidebar */}
      <div style={S.sidebar}>
        <div style={{ padding: '24px 20px', borderBottom: '1px solid #334155' }}>
          <div style={{ fontSize: 20, fontWeight: 900, color: '#fff' }}>TandaClick</div>
          <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>Panel de administración</div>
        </div>
        <div style={{ padding: '12px 0' }}>
          {secciones.map(s => (
            <div key={s.id} onClick={() => setSeccion(s.id)} style={{
              padding: '12px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
              background: seccion === s.id ? '#334155' : 'transparent',
              borderLeft: seccion === s.id ? '3px solid #3B82F6' : '3px solid transparent',
            }}>
              <span style={{ fontSize: 18 }}>{s.icon}</span>
              <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: seccion === s.id ? '#fff' : '#94A3B8' }}>{s.label}</span>
              {s.count > 0 && <span style={{ background: '#DC2626', color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99 }}>{s.count}</span>}
            </div>
          ))}
        </div>
        <div style={{ marginTop: 'auto', padding: '16px 20px', borderTop: '1px solid #334155', fontSize: 12, color: '#64748B' }}>
          {usuario?.nombre}
        </div>
      </div>

      {/* Main content */}
      <div style={S.main}>
        <div style={S.header}>
          <h1 style={{ fontSize: 22, fontWeight: 900, color: C.text, margin: 0 }}>
            {secciones.find(s => s.id === seccion)?.icon} {secciones.find(s => s.id === seccion)?.label}
          </h1>
          <div style={{ display: 'flex', gap: 8 }}>
            {seccion === 'tandas' && (
              <button onClick={() => { setFormTanda({ nombre: '', monto: '', semanas: '', clabe: CLABE_DEFAULT }); setModalCrear(true); }} style={S.btnPrimary}>+ Crear tanda</button>
            )}
            <button onClick={async () => { await cargarTodo(); showToast('Datos actualizados'); }} style={S.refreshBtn}>Actualizar</button>
          </div>
        </div>

        {/* ── TANDAS ── */}
        {seccion === 'tandas' && (
          <div>
            {tandas.length === 0 && <div style={S.empty}>No hay tandas creadas</div>}
            {tandas.map(t => {
              const expanded = tandaExpandida === t.id;
              const participantes = t.participantes || [];
              return (
                <div key={t.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0', marginBottom: 8, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                  {/* Row header - clickable */}
                  <div onClick={() => setTandaExpandida(expanded ? null : t.id)} style={{
                    display: 'flex', alignItems: 'center', padding: '14px 18px', cursor: 'pointer', gap: 16,
                    background: expanded ? '#F8FAFC' : '#fff',
                  }}>
                    <span style={{ fontSize: 16, color: '#94A3B8', transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>▶</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 800, color: '#1E293B' }}>{t.nombre}</div>
                      <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>
                        {fmt(t.monto)}/{(t.frecuencia || 'Semanal').toLowerCase().replace('al', '')} · {t.semanas} {t.frecuencia === 'Mensual' ? 'meses' : t.frecuencia === 'Quincenal' ? 'quincenas' : 'semanas'} · Periodo {t.semana_actual} · {t.nivel_minimo || 'Bronce'}+
                      </div>
                    </div>
                    <div style={{ fontSize: 13, color: '#64748B', textAlign: 'right', flexShrink: 0 }}>
                      {participantes.length}/{t.semanas}
                    </div>
                    <span style={{ background: t.estado === 'activa' ? '#D1FAE5' : t.estado === 'pendiente' ? '#FEF3C7' : '#F1F5F9', color: t.estado === 'activa' ? '#059669' : t.estado === 'pendiente' ? '#D97706' : '#64748B', padding: '3px 12px', borderRadius: 99, fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{t.estado?.charAt(0).toUpperCase() + t.estado?.slice(1)}</span>
                  </div>

                  {/* Alerta de pagos */}
                  {alertasTandas[t.id] && (
                    <div style={{
                      padding: '10px 18px', fontSize: 13, fontWeight: 600,
                      background: alertasTandas[t.id].tipo === 'retraso' ? '#FEE2E2' : alertasTandas[t.id].tipo === 'auto' ? '#D1FAE5' : alertasTandas[t.id].tipo === 'listo' ? '#DBEAFE' : '#FEF3C7',
                      color: alertasTandas[t.id].tipo === 'retraso' ? '#991B1B' : alertasTandas[t.id].tipo === 'auto' ? '#065F46' : alertasTandas[t.id].tipo === 'listo' ? '#1E40AF' : '#92400E',
                      borderTop: '1px solid #E2E8F0',
                    }}>
                      {alertasTandas[t.id].tipo === 'retraso' ? '⚠️ ' : alertasTandas[t.id].tipo === 'auto' ? '✅ ' : alertasTandas[t.id].tipo === 'listo' ? '✓ ' : '⏳ '}
                      {alertasTandas[t.id].msg}
                    </div>
                  )}

                  {/* Expanded detail */}
                  {expanded && (
                    <div style={{ borderTop: '1px solid #E2E8F0', padding: '16px 18px' }}>
                      {/* Info row */}
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                        {[
                          { label: 'Monto', value: fmt(t.monto) },
                          { label: 'Semanas', value: t.semanas },
                          { label: 'Semana actual', value: t.semana_actual },
                          { label: 'Tanda completa', value: fmt(t.monto * t.semanas) },
                          { label: 'CLABE', value: t.clabe || '—', mono: true },
                        ].map((item, i) => (
                          <div key={i} style={{ background: '#F8FAFC', borderRadius: 8, padding: '8px 14px', minWidth: 120 }}>
                            <div style={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{item.label}</div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#1E293B', fontFamily: item.mono ? 'monospace' : 'inherit', marginTop: 2 }}>{item.value}</div>
                          </div>
                        ))}
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                        {t.semana_actual > 1 && (
                          <button onClick={async (e) => {
                            e.stopPropagation();
                            if (!window.confirm(`¿Regresar a semana ${t.semana_actual - 1}?`)) return;
                            await supabase.from('tandas').update({ semana_actual: t.semana_actual - 1 }).eq('id', t.id);
                            showToast(`Regresó a semana ${t.semana_actual - 1}`); cargarTodo();
                          }} style={{ ...S.btnSmall, color: '#D97706', borderColor: '#FDE68A' }}>◀ Regresar a semana {t.semana_actual - 1}</button>
                        )}
                        {t.semana_actual < t.semanas && t.estado !== 'terminada' && (
                          <button onClick={(e) => { e.stopPropagation(); abrirModalAvanzar(t); }} style={S.btnSmall}>▶ Avanzar a semana {t.semana_actual + 1}</button>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); abrirEditar(t); }} style={S.btnSmall}>Editar tanda</button>
                        <button onClick={(e) => { e.stopPropagation(); eliminarTanda(t); }} style={{ ...S.btnSmall, color: '#DC2626', borderColor: '#FCA5A5' }}>Eliminar tanda</button>
                      </div>

                      {/* Pago al ganador de esta semana */}
                      {(() => {
                        const ganador = participantes.find(p => p.turno === t.semana_actual);
                        if (!ganador) return null;
                        const montoTotal = t.monto * t.semanas;
                        const comision = Math.round(montoTotal * 0.03);
                        const montoNeto = montoTotal - comision;
                        const metodoCobro = ganador.usuarios?.metodo_cobro || 'spei';
                        return (
                          <div style={{ background: '#FFFBEB', border: '1.5px solid #FDE68A', borderRadius: 12, padding: '14px 16px', marginBottom: 16 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#92400E', marginBottom: 8, textTransform: 'uppercase' }}>
                              💰 Pago al ganador — Semana {t.semana_actual}
                            </div>
                            <div style={{ fontSize: 14, fontWeight: 800, color: '#1E293B', marginBottom: 4 }}>{ganador.usuarios?.nombre}</div>
                            <div style={{ fontSize: 13, color: '#64748B', marginBottom: 8 }}>
                              Turno #{ganador.turno} · {metodoCobro === 'oxxo' ? 'Cobro OXXO' : 'SPEI'} · Monto: {fmt(montoNeto)} ({fmt(montoTotal)} - {fmt(comision)} comisión)
                            </div>
                            {metodoCobro === 'spei' && (
                              <div style={{ fontSize: 12, color: '#475569', marginBottom: 10, fontFamily: 'monospace' }}>
                                CLABE: {ganador.usuarios?.clabe || '—'} · {ganador.usuarios?.banco || '—'}
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: 8 }}>
                              {metodoCobro === 'spei' ? (
                                <button onClick={async (e) => {
                                  e.stopPropagation();
                                  if (!window.confirm(`¿Confirmar transferencia SPEI de ${fmt(montoNeto)} a ${ganador.usuarios?.nombre}?`)) return;
                                  try {
                                    const { data: { session } } = await supabase.auth.getSession();
                                    const ganadorNombre = ganador.usuarios?.nombre?.split(' ')[0] || 'Participante';

                                    // TODO: mover envío de email a endpoint backend /api/pago-ganador
                                    // TODO: obtener emails vía endpoint backend (auth.admin.listUsers requiere service_role)

                                    // Notificar al ganador vía backend (email + WhatsApp)
                                    try {
                                      await fetch('https://tandamx-bot.vercel.app/api/pago-ganador', {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
                                        body: JSON.stringify({ participante_id: ganador.id, tipo: 'spei' })
                                      });
                                    } catch (_) {}

                                    // Avanzar semana
                                    if (t.semana_actual < t.semanas) {
                                      await supabase.from('tandas').update({ semana_actual: t.semana_actual + 1 }).eq('id', t.id);
                                      showToast(`Pago confirmado a ${ganadorNombre} — Semana avanzada a ${t.semana_actual + 1}`);
                                    } else {
                                      await supabase.from('tandas').update({ estado: 'terminada' }).eq('id', t.id);
                                      showToast(`Pago confirmado a ${ganadorNombre} — Tanda terminada`);
                                    }
                                    cargarTodo();
                                  } catch (err) { showToast('Error: ' + err.message); }
                                }} style={{ ...S.btnSmall, background: '#059669', color: '#fff', flex: 1, padding: '10px 0' }}>
                                  ✅ Transferencia SPEI enviada
                                </button>
                              ) : (
                                <label style={{ ...S.btnSmall, background: '#D97706', color: '#fff', flex: 1, padding: '10px 0', textAlign: 'center', cursor: 'pointer', display: 'block' }}>
                                  📱 Subir código QR OXXO
                                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async (ev) => {
                                    const file = ev.target.files[0]; if (!file) return;
                                    ev.target.value = '';
                                    try {
                                      showToast('Subiendo QR...');
                                      // Subir imagen a Storage
                                      const fileName = `qr_oxxo_${ganador.id}_${Date.now()}.${file.name.split('.').pop()}`;
                                      const { error: upErr } = await supabase.storage.from('comprobantes').upload(fileName, file);
                                      if (upErr) { showToast('Error al subir: ' + upErr.message); return; }
                                      const { data: urlData } = supabase.storage.from('comprobantes').getPublicUrl(fileName);
                                      const qrUrl = urlData?.publicUrl;
                                      if (!qrUrl) { showToast('Error obteniendo URL'); return; }

                                      // TODO: mover envío de email a endpoint backend /api/pago-ganador
                                      // TODO: obtener emails vía endpoint backend (auth.admin.listUsers requiere service_role)
                                      const { data: { session } } = await supabase.auth.getSession();
                                      const ganadorNombre = ganador.usuarios?.nombre?.split(' ')[0] || 'Participante';

                                      // Notificar al ganador vía backend (email + WhatsApp)
                                      try {
                                        await fetch('https://tandamx-bot.vercel.app/api/pago-ganador', {
                                          method: 'POST',
                                          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
                                          body: JSON.stringify({ participante_id: ganador.id, tipo: 'oxxo', qr_url: qrUrl })
                                        });
                                      } catch (_) {}

                                      // Avanzar semana
                                      if (t.semana_actual < t.semanas) {
                                        await supabase.from('tandas').update({ semana_actual: t.semana_actual + 1 }).eq('id', t.id);
                                        showToast(`QR enviado a ${ganadorNombre} — Semana avanzada a ${t.semana_actual + 1}`);
                                      } else {
                                        await supabase.from('tandas').update({ estado: 'terminada' }).eq('id', t.id);
                                        showToast(`QR enviado a ${ganadorNombre} — Tanda terminada`);
                                      }
                                      cargarTodo();
                                    } catch (err) { showToast('Error: ' + err.message); }
                                  }} />
                                </label>
                              )}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Participantes */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Participantes ({participantes.length}/{t.semanas})
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); setModalAgregarUsuario(t.id); setUsuarioSeleccionado(''); }} style={S.btnSmall}>+ Agregar usuario</button>
                      </div>
                      {participantes.length === 0 ? (
                        <div style={{ fontSize: 13, color: '#94A3B8', padding: '12px 0' }}>Sin participantes aún</div>
                      ) : (
                        <table style={{ ...S.table, boxShadow: 'none', border: '1px solid #E2E8F0' }}>
                          <thead><tr>{['Turno', 'Nombre', 'Teléfono', 'Estado', ''].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                          <tbody>
                            {participantes.sort((a, b) => (a.turno || 99) - (b.turno || 99)).map(p => (
                              <tr key={p.id} style={S.tr}>
                                <td style={S.td}>{p.turno || '—'}</td>
                                <td style={S.td}>{p.usuarios?.nombre || '—'}</td>
                                <td style={S.td}>{p.usuarios?.telefono || '—'}</td>
                                <td style={S.td}>
                                  {!p.turno ? (
                                    <span style={{ fontSize: 11, color: '#94A3B8' }}>Sin turno</span>
                                  ) : p.turno === t.semana_actual ? (
                                    <span style={{ background: '#D1FAE5', color: '#059669', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700 }}>Cobra esta semana</span>
                                  ) : p.turno < t.semana_actual ? (
                                    <span style={{ fontSize: 11, color: '#94A3B8' }}>Ya cobró</span>
                                  ) : (
                                    <span style={{ fontSize: 11, color: '#64748B' }}>Pendiente</span>
                                  )}
                                </td>
                                <td style={S.td}>
                                  <button onClick={() => quitarParticipante(p.id, p.usuarios?.nombre, p.usuario_id, t.id)} style={{ ...S.btnSmall, color: '#DC2626', borderColor: '#FCA5A5', fontSize: 11 }}>Quitar</button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── USUARIOS ── */}
        {seccion === 'usuarios' && (
          <div>
            {usuarios.length === 0 && <div style={S.empty}>No hay usuarios registrados</div>}
            {usuarios.map(u => {
              const expanded = usuarioExpandido === u.id;
              return (
                <div key={u.id} style={{ background: u.rol === 'inhabilitado' ? '#FEF2F2' : '#fff', borderRadius: 12, border: '1px solid #E2E8F0', marginBottom: 8, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                  {/* Row header */}
                  <div onClick={() => expandirUsuario(u)} style={{ display: 'flex', alignItems: 'center', padding: '14px 18px', cursor: 'pointer', gap: 14, background: expanded ? '#F8FAFC' : 'transparent' }}>
                    <span style={{ fontSize: 14, color: '#94A3B8', transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>▶</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 800, color: '#1E293B' }}>{u.nombre}</div>
                      <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>{u.telefono} · Cliente: {u.codigo_ref || '—'}</div>
                    </div>
                    <span style={{ background: '#EFF6FF', color: '#1A56DB', padding: '2px 10px', borderRadius: 99, fontSize: 12, fontWeight: 700 }}>{u.nivel || 'Bronce'}</span>
                    {u.rol === 'inhabilitado' ? (
                      <span style={{ background: '#FEE2E2', color: '#DC2626', padding: '2px 10px', borderRadius: 99, fontSize: 12, fontWeight: 700 }}>Inhabilitado</span>
                    ) : u.rol === 'admin' ? (
                      <span style={{ background: '#DBEAFE', color: '#1D4ED8', padding: '2px 10px', borderRadius: 99, fontSize: 12, fontWeight: 700 }}>Admin</span>
                    ) : (
                      <span style={{ background: '#D1FAE5', color: '#059669', padding: '2px 10px', borderRadius: 99, fontSize: 12, fontWeight: 700 }}>Activo</span>
                    )}
                  </div>

                  {/* Expanded detail */}
                  {expanded && (
                    <div style={{ borderTop: '1px solid #E2E8F0', padding: '16px 18px' }}>
                      {/* Info cards */}
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                        {[
                          { label: 'No. Cliente', value: u.codigo_ref || '—', mono: true },
                          { label: 'Nivel', value: u.nivel || 'Bronce' },
                          { label: 'Puntos', value: u.puntos || 0 },
                          { label: 'Tandas completadas', value: u.tandas_completadas || 0 },
                          { label: 'Método cobro', value: u.metodo_cobro === 'oxxo' ? 'OXXO' : 'SPEI' },
                          { label: 'CLABE', value: u.clabe || '—', mono: true },
                          { label: 'Banco', value: u.banco || '—' },
                          { label: 'Titular', value: u.nombre_titular || u.nombre },
                          { label: 'Registrado', value: u.created_at ? new Date(u.created_at).toLocaleDateString('es-MX') : '—' },
                        ].map((item, i) => (
                          <div key={i} style={{ background: '#F8FAFC', borderRadius: 8, padding: '8px 14px', minWidth: 130 }}>
                            <div style={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{item.label}</div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#1E293B', fontFamily: item.mono ? 'monospace' : 'inherit', marginTop: 2 }}>{item.value}</div>
                          </div>
                        ))}
                      </div>

                      {/* Asignar referencia si no tiene */}
                      {!u.codigo_ref && (
                        <button onClick={() => asignarNumCliente(u)} style={{ ...S.btnSmall, marginBottom: 12 }}>Asignar No. Cliente</button>
                      )}

                      {/* Verificación de identidad — por documento */}
                      {(u.ine_frente_url || u.ine_reverso_url || u.selfie_url) && (
                        <div style={{ marginBottom: 16 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#64748B', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Verificación de identidad
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {[
                              { label: 'INE Frente', url: u.ine_frente_url, campo: 'ine_frente_status' },
                              { label: 'INE Reverso', url: u.ine_reverso_url, campo: 'ine_reverso_status' },
                              { label: 'Selfie', url: u.selfie_url, campo: 'selfie_status' },
                            ].map((doc, i) => {
                              const status = u[doc.campo] || 'pendiente';
                              return (
                                <div key={i} style={{ background: '#F8FAFC', borderRadius: 10, border: '1px solid #E2E8F0', padding: 12 }}>
                                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                                    {/* Thumbnail */}
                                    {doc.url ? (
                                      <img src={doc.url} alt={doc.label} onClick={() => window.open(doc.url, '_blank')}
                                        style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 6, border: '1px solid #E2E8F0', cursor: 'pointer', flexShrink: 0 }} />
                                    ) : (
                                      <div style={{ width: 80, height: 60, borderRadius: 6, border: '1px dashed #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#94A3B8', flexShrink: 0 }}>No subida</div>
                                    )}
                                    {/* Info + acciones */}
                                    <div style={{ flex: 1 }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                        <div style={{ fontSize: 13, fontWeight: 700, color: '#1E293B' }}>{doc.label}</div>
                                        <span style={{
                                          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                                          background: status === 'aprobado' ? '#D1FAE5' : status === 'rechazado' ? '#FEE2E2' : '#FEF3C7',
                                          color: status === 'aprobado' ? '#059669' : status === 'rechazado' ? '#DC2626' : '#D97706',
                                        }}>{status === 'aprobado' ? 'Aprobado' : status === 'rechazado' ? 'Rechazado' : 'Pendiente'}</span>
                                      </div>
                                      {doc.url && status !== 'aprobado' && (
                                        <div style={{ display: 'flex', gap: 6 }}>
                                          <button onClick={async () => {
                                            await supabase.from('usuarios').update({ [doc.campo]: 'aprobado' }).eq('id', u.id);
                                            showToast(doc.label + ' aprobado'); cargarTodo();
                                          }} style={{ ...S.btnSmall, background: '#059669', color: '#fff', fontSize: 11, padding: '4px 10px' }}>Aprobar</button>
                                          <button onClick={() => { setModalRechazoDoc({ usuario: u, tipo: doc.label, campo: doc.campo }); setMotivoRechazoDoc(''); setMotivoRechazoCustom(''); }}
                                            style={{ ...S.btnSmall, background: '#DC2626', color: '#fff', fontSize: 11, padding: '4px 10px' }}>Rechazar</button>
                                        </div>
                                      )}
                                      {status === 'aprobado' && <div style={{ fontSize: 11, color: '#059669' }}>✓ Verificado</div>}
                                      {status === 'rechazado' && <div style={{ fontSize: 11, color: '#DC2626' }}>Rechazado{u[doc.campo + '_motivo'] ? ': ' + u[doc.campo + '_motivo'] : ''}</div>}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                        <button onClick={() => setModalNivel(u)} style={S.btnSmall}>Cambiar nivel</button>
                        <button onClick={() => { setModalAmonestar(u); setMotivoAmonestacion(''); }} style={{ ...S.btnSmall, color: '#D97706', borderColor: '#FDE68A' }}>Amonestar</button>
                        <button onClick={() => inhabilitarUsuario(u)} style={{ ...S.btnSmall, color: u.rol === 'inhabilitado' ? '#059669' : '#DC2626', borderColor: u.rol === 'inhabilitado' ? '#BBF7D0' : '#FCA5A5' }}>
                          {u.rol === 'inhabilitado' ? 'Rehabilitar' : 'Inhabilitar'}
                        </button>
                        {u.rol !== 'admin' && (
                          <button onClick={() => darDeBajaUsuario(u)} style={{ ...S.btnSmall, background: '#DC2626', color: '#fff', borderColor: '#DC2626' }}>Baja definitiva</button>
                        )}
                      </div>

                      {/* Historial de tandas */}
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#64748B', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Historial de tandas ({historialUsuario.length})
                      </div>
                      {historialUsuario.length === 0 ? (
                        <div style={{ fontSize: 13, color: '#94A3B8', padding: '8px 0', marginBottom: 12 }}>No participa en ninguna tanda</div>
                      ) : (
                        <table style={{ ...S.table, boxShadow: 'none', border: '1px solid #E2E8F0', marginBottom: 16 }}>
                          <thead><tr>{['Tanda', 'Monto', 'Turno', 'Semana', 'Estado', 'Pagos realizados'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                          <tbody>
                            {historialUsuario.map(p => {
                              const pagosHechos = (p.pagos || []).filter(pg => pg.pagado).length;
                              const totalSemanas = p.tandas?.semanas || 0;
                              return (
                                <tr key={p.id} style={S.tr}>
                                  <td style={S.td}><strong>{p.tandas?.nombre}</strong></td>
                                  <td style={S.td}>{fmt(p.tandas?.monto)}</td>
                                  <td style={S.td}>{p.turno || '—'}</td>
                                  <td style={S.td}>{p.tandas?.semana_actual}/{totalSemanas}</td>
                                  <td style={S.td}>
                                    <span style={{
                                      background: p.tandas?.estado === 'activa' ? '#D1FAE5' : '#F1F5F9',
                                      color: p.tandas?.estado === 'activa' ? '#059669' : '#64748B',
                                      padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700
                                    }}>{p.tandas?.estado}</span>
                                  </td>
                                  <td style={S.td}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <div style={{ flex: 1, background: '#E2E8F0', borderRadius: 99, height: 6, overflow: 'hidden' }}>
                                        <div style={{ width: `${totalSemanas ? (pagosHechos / totalSemanas) * 100 : 0}%`, height: '100%', background: pagosHechos === totalSemanas && totalSemanas > 0 ? '#059669' : '#1A56DB', borderRadius: 99 }} />
                                      </div>
                                      <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>{pagosHechos}/{totalSemanas}</span>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}

                      {/* Amonestaciones e historial */}
                      {amonestacionesUsuario.length > 0 && (
                        <>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#DC2626', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Historial de amonestaciones ({amonestacionesUsuario.length})
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {amonestacionesUsuario.map(a => {
                              const det = typeof a.detalle === 'string' ? JSON.parse(a.detalle) : a.detalle;
                              return (
                                <div key={a.id} style={{ background: a.accion === 'amonestacion' ? '#FEF3C7' : '#FEE2E2', border: `1px solid ${a.accion === 'amonestacion' ? '#FDE68A' : '#FCA5A5'}`, borderRadius: 10, padding: '10px 14px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                    <span style={{ fontSize: 12, fontWeight: 700, color: a.accion === 'amonestacion' ? '#92400E' : '#991B1B' }}>
                                      {a.accion === 'amonestacion' ? 'Amonestación' : a.accion === 'usuario_inhabilitado' ? 'Inhabilitado' : 'Rehabilitado'}
                                    </span>
                                    <span style={{ fontSize: 11, color: '#94A3B8' }}>{new Date(a.created_at).toLocaleString('es-MX')}</span>
                                  </div>
                                  {det?.motivo && <div style={{ fontSize: 13, color: '#475569' }}>{det.motivo}</div>}
                                  {det?.admin && <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>Por: {det.admin}</div>}
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── SOLICITUDES ── */}
        {seccion === 'solicitudes' && (
          <div>
            {solicitudes.length === 0 && <div style={S.empty}>No hay solicitudes pendientes</div>}
            {solicitudes.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <table style={S.table}>
                  <thead>
                    <tr>{['Fecha', 'Usuario', 'Tanda', 'Monto', 'Acciones'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {solicitudes.map(s => {
                      const det = typeof s.detalle === 'string' ? JSON.parse(s.detalle) : s.detalle;
                      const usr = usuarios.find(u => u.id === s.usuario_id);
                      return (
                        <tr key={s.id} style={S.tr}>
                          <td style={S.td}>{new Date(s.created_at).toLocaleString('es-MX')}</td>
                          <td style={S.td}><strong>{usr?.nombre || s.usuario_id?.slice(0, 8)}</strong><br/><span style={{ fontSize: 11, color: '#94A3B8' }}>{usr?.telefono}</span></td>
                          <td style={S.td}>{det?.tanda_nombre || det?.tanda_id?.slice(0, 8)}</td>
                          <td style={S.td}>{det?.monto ? fmt(det.monto) : '—'}</td>
                          <td style={S.td}>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button onClick={async () => {
                                const { error } = await supabase.from('participantes').insert({
                                  usuario_id: s.usuario_id, tanda_id: det?.tanda_id,
                                });
                                if (error) { showToast('Error: ' + error.message); return; }
                                await supabase.from('auditoria').delete().eq('id', s.id);
                                // Verificar si la tanda se llenó (consultar DB directo)
                                const { data: tandaDB2 } = await supabase.from('tandas').select('semanas').eq('id', det?.tanda_id).single();
                                const { data: allP } = await supabase.from('participantes').select('id').eq('tanda_id', det?.tanda_id);
                                if (tandaDB2 && allP && allP.length >= tandaDB2.semanas) {
                                  await autoSorteo(det.tanda_id);
                                } else {
                                  showToast('Participante agregado');
                                }
                                cargarTodo();
                              }} style={{ ...S.btnSmall, background: '#059669', color: '#fff' }}>Aprobar</button>
                              <button onClick={async () => {
                                await supabase.from('auditoria').delete().eq('id', s.id);
                                showToast('Solicitud rechazada'); cargarTodo();
                              }} style={{ ...S.btnSmall, background: '#DC2626', color: '#fff' }}>Rechazar</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── PAGOS (agrupados por tanda) ── */}
        {seccion === 'pagos' && (() => {
          // Agrupar pagos por tanda
          const porTanda = {};
          pagos.forEach(p => {
            const tandaNombre = p.participantes?.tandas?.nombre || 'Sin tanda';
            const tandaId = p.participantes?.tanda_id || tandaNombre;
            if (!porTanda[tandaId]) porTanda[tandaId] = { nombre: tandaNombre, monto: p.participantes?.tandas?.monto, pagos: [] };
            porTanda[tandaId].pagos.push(p);
          });
          const grupos = Object.entries(porTanda);

          return (
            <div>
              {grupos.length === 0 && <div style={S.empty}>No hay comprobantes registrados</div>}
              {grupos.map(([tandaId, grupo]) => {
                const expanded = pagoTandaExpandida === tandaId;
                const pendientes = grupo.pagos.filter(p => !p.pagado && p.estado_comprobante !== 'rechazado').length;
                return (
                  <div key={tandaId} style={{ background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0', marginBottom: 8, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                    <div onClick={() => setPagoTandaExpandida(expanded ? null : tandaId)} style={{
                      display: 'flex', alignItems: 'center', padding: '14px 18px', cursor: 'pointer', gap: 14,
                      background: expanded ? '#F8FAFC' : '#fff',
                    }}>
                      <span style={{ fontSize: 14, color: '#94A3B8', transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>▶</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 15, fontWeight: 800, color: '#1E293B' }}>{grupo.nombre}</div>
                        <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>{grupo.pagos.length} comprobantes · {fmt(grupo.monto)}/semana</div>
                      </div>
                      {pendientes > 0 && (
                        <span style={{ background: '#FEF3C7', color: '#D97706', padding: '3px 12px', borderRadius: 99, fontSize: 12, fontWeight: 700 }}>{pendientes} pendientes</span>
                      )}
                    </div>

                    {expanded && (
                      <div style={{ borderTop: '1px solid #E2E8F0', overflowX: 'auto' }}>
                        <table style={{ ...S.table, borderRadius: 0, boxShadow: 'none' }}>
                          <thead>
                            <tr>{['Fecha', 'Usuario', 'Semana', 'Estado', 'Comprobante', 'Acciones'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
                          </thead>
                          <tbody>
                            {grupo.pagos.map(p => (
                              <tr key={p.id} style={S.tr}>
                                <td style={S.td}>{new Date(p.created_at).toLocaleDateString('es-MX')}</td>
                                <td style={S.td}><strong>{p.participantes?.usuarios?.nombre}</strong></td>
                                <td style={S.td}>Semana {p.semana}</td>
                                <td style={S.td}>
                                  <span style={{
                                    background: p.pagado ? '#D1FAE5' : p.estado_comprobante === 'rechazado' ? '#FEE2E2' : '#FEF3C7',
                                    color: p.pagado ? '#059669' : p.estado_comprobante === 'rechazado' ? '#DC2626' : '#D97706',
                                    padding: '2px 10px', borderRadius: 99, fontSize: 12, fontWeight: 700
                                  }}>{p.pagado ? 'Confirmado' : p.estado_comprobante === 'rechazado' ? 'Rechazado' : 'Pendiente'}</span>
                                </td>
                                <td style={S.td}>
                                  {p.comprobante_url && (
                                    <a href={getComprobanteUrl(p.comprobante_url)} target="_blank" rel="noreferrer" style={{ color: '#1A56DB', fontSize: 12, fontWeight: 600 }}>Ver comprobante</a>
                                  )}
                                </td>
                                <td style={S.td}>
                                  {!p.pagado && p.estado_comprobante !== 'rechazado' && (
                                    <div style={{ display: 'flex', gap: 6 }}>
                                      <button onClick={async () => {
                                        await supabase.from('pagos').update({ pagado: true, fecha_pago: new Date().toISOString() }).eq('id', p.id);
                                        showToast('Pago confirmado'); cargarTodo();
                                      }} style={{ ...S.btnSmall, background: '#059669', color: '#fff' }}>Confirmar</button>
                                      <button onClick={() => { setModalRechazoPago(p.id); setMotivoRechazoPago(''); setMotivoRechazoPagoCustom(''); }}
                                        style={{ ...S.btnSmall, background: '#DC2626', color: '#fff' }}>Rechazar</button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

// ── Estilos ──
const S = {
  layout: { display: 'flex', minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif", background: '#F8FAFC', overflow: 'hidden' },
  sidebar: { width: 240, background: '#1E293B', display: 'flex', flexDirection: 'column', position: 'fixed', top: 0, bottom: 0, left: 0, zIndex: 50, flexShrink: 0 },
  main: { flex: 1, marginLeft: 240, padding: '24px 32px', overflowX: 'auto', overflowY: 'auto', maxHeight: '100vh' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
  center: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', fontSize: 16, color: '#64748B', fontFamily: "'Plus Jakarta Sans', sans-serif" },
  toast: { position: 'fixed', top: 16, right: 16, background: '#1E293B', color: '#fff', padding: '12px 20px', borderRadius: 10, fontSize: 13, fontWeight: 600, zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.2)' },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  th: { textAlign: 'left', padding: '12px 14px', fontSize: 11, fontWeight: 700, color: '#64748B', background: '#F1F5F9', borderBottom: '1px solid #E2E8F0', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' },
  td: { padding: '12px 14px', fontSize: 13, color: '#1E293B', borderBottom: '1px solid #F1F5F9', verticalAlign: 'top' },
  tr: { transition: 'background 0.1s' },
  input: { padding: '8px 12px', borderRadius: 8, border: '1.5px solid #E2E8F0', fontSize: 13, outline: 'none', fontFamily: "'Plus Jakarta Sans', sans-serif", boxSizing: 'border-box' },
  btnPrimary: { padding: '8px 16px', borderRadius: 8, border: 'none', background: '#1A56DB', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", whiteSpace: 'nowrap' },
  btnSmall: { padding: '6px 12px', borderRadius: 6, border: '1px solid #E2E8F0', background: '#F8FAFC', color: '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", whiteSpace: 'nowrap' },
  refreshBtn: { padding: '8px 16px', borderRadius: 8, border: '1px solid #E2E8F0', background: '#fff', color: '#475569', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif" },
  empty: { textAlign: 'center', padding: 40, color: '#94A3B8', fontSize: 14 },
};

export { Dashboard };
